import { type FC, Suspense, lazy, useCallback, useEffect, useState } from 'react'

import './_userCard.scss'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import * as spriteIcons from 'assets/svg/sprite'
import {
  IconCameraOutline,
  IconImageOutline,
  IconPlayOutline
} from 'assets/svg/sprite'
import FullscreenMessage from 'features/Messaging/Message/components/FullscreenMessage/FullscreenMessage'
import PostModal from 'pages/postModal'

import style from '../../../pages/vault/components/VaultCardGrouped/_vault_card_grouped.module.scss'
import AvatarHolder from '../AvatarHolder/AvatarHolder'

type UserCardContentProps = {
  thumb_src: any
  cardContent: JSX.Element | JSX.Element[]
  hasDropDown?: boolean
  dropDownContent?: JSX.Element | JSX.Element[]
  customIconClass?: string
  iconSize?: string
  card?: any
  index?: any
  previewType?: 'post' | 'message' | 'story'
}

const UserCardContent: FC<UserCardContentProps> = ({
  thumb_src,
  cardContent,
  hasDropDown,
  dropDownContent,
  customIconClass,
  iconSize,
  card,
  previewType
}) => {

  
  const [dropDownOpen, setDropDownOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const { t } = useTranslation()

  const media = card || {}

  useEffect(() => {
    if (thumb_src === '') setPreviewOpen(false)
  }, [thumb_src])

  const combinedMedia: any[] = []
  if (card) {
    combinedMedia.push(card?.videos)
    combinedMedia.push(card?.photos)
  }
  if (media?.message?.message) {
    combinedMedia.push(media.message.message.photos)
    combinedMedia.push(media.message.message.videos)
  }

  const mergedMedia: any[] = ([] as any[]).concat.apply([], combinedMedia)

  const isMessageCard = previewType === 'message'
  const isStoryCard = previewType === 'story'

  const renderDateTags = () => {
    const date = new Date(media?.created_at)
    const monthArray = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'July',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ]

    return (
      <div className={style.media_tags_date_container}>
        <div className={style.media_tags_background}></div>
        <div className={style.media_tags_date_text}>
          {monthArray[date.getMonth()]} {date.getDate()}
        </div>
      </div>
    )
  }

  const renderTags = () => {
    if (!card) return null

    const videosCount = Array.isArray(card?.videos) ? card.videos.length : 0
    const photosCount = Array.isArray(card?.photos) ? card.photos.length : 0

    if (videosCount > 0 || photosCount > 0) {
      return (
        <div className={style.analytics_media_bottom_tags_container}>
          {((videosCount > 0 && photosCount > 0) ||
            (videosCount > 1 && photosCount === 0)) && (
            <div className={style.analytics_media_bottom_tag}>
              <div className={style.analytics_media_bottom_tag_text}>
                <IconCameraOutline width="16" height="12" color="white" />
                <span>{videosCount}</span>
              </div>
            </div>
          )}

          {((photosCount > 0 && videosCount > 0) ||
            (videosCount === 0 && photosCount > 1)) && (
            <div className={style.analytics_media_bottom_tag}>
              <div className={style.analytics_media_bottom_tag_text}>
                <IconImageOutline width="12" height="12" color="#ffffff" />
                <span>{photosCount}</span>
              </div>
            </div>
          )}
        </div>
      )
    }

    // Keep this here in case some callers still rely on a top-level video_src
    if (card?.video_src) {
      return (
        <div className={style.analytics_media_bottom_tags_container}>
          <div className={style.analytics_media_bottom_tag}>
            <div className={style.analytics_media_bottom_tag_text}>
              <IconCameraOutline width="16" height="12" color="white" />
              <span>1</span>
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  //  Thumb-only rendering (no ReactPlayer)
  const renderCardMedia = () => {
    // 1) PHOTO (card.photos)
    if (card?.photos?.length > 0) {
      const p0 = card.photos[0]
      const src =
        p0?.thumb_url || p0?.url || card?.image_src || thumb_src || ''
      if (!src) return null
      return <img className={style.media_image} src={src} />
    }

    // 2) VIDEO (card.videos) — poster only + play overlay
    if (card?.videos?.length > 0) {
      const v0 = card.videos[0]
      const poster =
        v0?.active_thumb ||
        card?.image_src ||
        (mergedMedia?.find((m: any) => m?.active_thumb)?.active_thumb) ||
        thumb_src ||
        ''

      return (
        <>
          {poster ? (
            <img className={style.media_image} src={poster} />
          ) : (
            <div className={style.media_image} />
          )}

          {/* StopPropagation so clicking play icon doesn’t feel like “open modal” */}
          <div
            className={style.video_play}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <IconPlayOutline color="#ffffff" />
          </div>
        </>
      )
    }

    // 3) MESSAGE VIDEO — poster only + play overlay
    if (card?.message?.message?.videos?.length > 0) {
      const v0 = card.message.message.videos[0]
      const poster = v0?.active_thumb || card?.image_src || thumb_src || ''

      return (
        <>
          {poster ? (
            <img className={style.media_image} src={poster} />
          ) : (
            <div className={style.media_image} />
          )}
          <div
            className={style.video_play}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <IconPlayOutline color="#ffffff" />
          </div>
        </>
      )
    }

    // 4) MESSAGE PHOTO
    if (card?.message?.message?.photos?.length > 0) {
      const p0 = card.message.message.photos[0]
      const src =
        p0?.thumb_url || p0?.url || card?.image_src || thumb_src || ''
      if (!src) return null
      return <img className={style.media_image} src={src} />
    }

    return null
  }

  return (
    <div className="userCard userCard--earnings">
      <div className="userCard--earnings--content">
      <div
        className="userCard__avatar"
        onClick={() => {
          if (isStoryCard) return

          if (thumb_src !== '') {
            setPreviewOpen(true)
          }
        }}
      >
          {thumb_src === '' ? (
            <AvatarHolder
              img={thumb_src}
              size={iconSize ?? '60'}
              customClass={customIconClass ?? ''}
            />
          ) : (
            <Content>
              {renderTags()}
              {renderDateTags()}
              {renderCardMedia()}
            </Content>
          )}
        </div>

      {previewOpen &&
        (isMessageCard ? (
          <FullscreenMessage
            message={card}
            media={[
              ...(card?.photos ?? []),
              ...(card?.videos ?? []),
              ...(card?.photos_preview ?? []),
              ...(card?.videos_preview ?? [])
            ]}
            closeFn={() => {
              setPreviewOpen(false)
            }}
            startingIndex={0}
            purchaseMessage={() => {}}
          />
        ) : (
          <PostModal
            postId={card?.id}
            post={card}
            closeFn={() => {
              setPreviewOpen(false)
            }}
          />
        ))}

        {cardContent}

        {hasDropDown && (
          <button
            className={`earnings-dropdown-button ${
              dropDownOpen && 'earnings-dropdown-button-open'
            }`}
            onClick={() => {
              setDropDownOpen(!dropDownOpen)
            }}
          >
            <div className="earnings-dropdown-button-icon">
              <spriteIcons.IconDownChevron />
            </div>
          </button>
        )}
      </div>

      {hasDropDown && (
        <div
          className={`earnings-dropdown ${
            dropDownOpen && 'earnings-dropdown-open'
          }`}
        >
          {dropDownContent ?? t('noContent')}
        </div>
      )}
    </div>
  )
}

const Content = styled.div`
  height: 100px;
  width: 100px;
  border-radius: 2rem;
  overflow: hidden;
  border: 0.3rem solid transparent;
  transition: all ease-in-out 0.3s;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  img,
  video {
    height: 100%;
    width: 100%;
    object-fit: cover;
    border-radius: 2rem;
    outline: none;
    -webkit-tap-highlight-color: transparent;
  }

  video {
    display: block;
  }
`

export default UserCardContent